import { doc, getDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { RevisionLog, RevisionType } from '@/types';
import { ensureDayExists } from './days.repository';

// ============================================
// Collection Path Helpers
// ============================================

// Revision logs are stored in day document: users/{uid}/days/{date}.revisionLogs[]
const getDayRef = (userId: string, date: string) => doc(db, 'users', userId, 'days', date);

// ============================================
// Helper Functions
// ============================================

export const generateRevisionId = (): string => {
  return `rev_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// ============================================
// Input Types
// ============================================

export interface AddRevisionInput {
  type: RevisionType;
  blockId: string;
  blockTitle: string;

  // For moves
  fromStartTime?: number;
  fromDuration?: number;
  toStartTime?: number;
  toDuration?: number;

  // For resizes
  oldDuration?: number;
  newDuration?: number;

  // For deletes
  deletedStartTime?: number;
  deletedDuration?: number;

  // For adds
  addedStartTime?: number;
  addedDuration?: number;

  // Optional context
  reason?: string;
  planVersion?: number;
}

// ============================================
// CRUD Operations
// ============================================

export async function getRevisionLogs(userId: string, date: string): Promise<RevisionLog[]> {
  const dayRef = getDayRef(userId, date);
  const snapshot = await getDoc(dayRef);

  if (!snapshot.exists()) {
    return [];
  }

  const data = snapshot.data();
  const revisions: RevisionLog[] = data?.revisionLogs || [];

  // Sort by timestamp (newest first)
  return [...revisions].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

export async function addRevisionLog(
  userId: string,
  date: string,
  revision: AddRevisionInput
): Promise<void> {
  // Ensure day document exists
  await ensureDayExists(userId, date);

  // Build revision object, only including defined fields (Firestore rejects undefined)
  const newRevision: RevisionLog = {
    id: generateRevisionId(),
    date,
    timestamp: new Date(),
    type: revision.type,
    blockId: revision.blockId,
    blockTitle: revision.blockTitle,
    ...(revision.fromStartTime !== undefined && { fromStartTime: revision.fromStartTime }),
    ...(revision.fromDuration !== undefined && { fromDuration: revision.fromDuration }),
    ...(revision.toStartTime !== undefined && { toStartTime: revision.toStartTime }),
    ...(revision.toDuration !== undefined && { toDuration: revision.toDuration }),
    ...(revision.oldDuration !== undefined && { oldDuration: revision.oldDuration }),
    ...(revision.newDuration !== undefined && { newDuration: revision.newDuration }),
    ...(revision.deletedStartTime !== undefined && { deletedStartTime: revision.deletedStartTime }),
    ...(revision.deletedDuration !== undefined && { deletedDuration: revision.deletedDuration }),
    ...(revision.addedStartTime !== undefined && { addedStartTime: revision.addedStartTime }),
    ...(revision.addedDuration !== undefined && { addedDuration: revision.addedDuration }),
    ...(revision.reason !== undefined && { reason: revision.reason }),
    ...(revision.planVersion !== undefined && { planVersion: revision.planVersion }),
  };

  const dayRef = getDayRef(userId, date);

  // Get current revisions from Firestore to avoid race conditions
  const daySnapshot = await getDoc(dayRef);
  const existingRevisions = daySnapshot.data()?.revisionLogs || [];

  await updateDoc(dayRef, {
    revisionLogs: [newRevision, ...existingRevisions],
    updatedAt: serverTimestamp(),
  });
}

export async function clearRevisionLogs(userId: string, date: string): Promise<void> {
  const dayRef = getDayRef(userId, date);
  await updateDoc(dayRef, {
    revisionLogs: [],
    updatedAt: serverTimestamp(),
  });
}

// ============================================
// Subscriptions
// ============================================

export function subscribeToRevisionLogs(
  userId: string,
  date: string,
  onData: (logs: RevisionLog[]) => void,
  onError?: (error: Error) => void
): () => void {
  const dayRef = getDayRef(userId, date);

  return onSnapshot(
    dayRef,
    (snapshot) => {
      const dayData = snapshot.data();
      const revisionsData: RevisionLog[] = dayData?.revisionLogs || [];

      // Sort by timestamp (newest first)
      const sortedRevisions = [...revisionsData].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      onData(sortedRevisions);
    },
    (error) => {
      console.error('Error listening to revision logs:', error);
      onError?.(error);
    }
  );
}
